/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import upperFirst from 'lodash/upperFirst';

// UI lib components
import { Select, Text } from '@mantine/core';

// Lib types
import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  SelectProps,
} from '@mantine/core';

// Local Types & API
import {
  QuestionResolutionStatusSelectProps,
  QuestionResolutionStatusOption,
} from './type';
import { QuestionResolutionStatus } from '@app/types/blackroom-api';

// Styles
import colors, { palette } from '@app/shared/ui-kit/colors';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function QuestionResolutionStatusSelect({
  selectedResolutionStatus,
  ...props
}: QuestionResolutionStatusSelectProps) {
  /* ********************************** HOOKS ********************************* */

  // Localization
  const { t } = useTranslation(['Support']);

  const questionResolutionStatusOptions: QuestionResolutionStatusOption[] =
    useMemo(
      () => [
        {
          label: upperFirst(t('Support:Common.resolutionStatus.open')),
          color: palette.blackroomGreen,
          value: QuestionResolutionStatus.Open,
        },
        {
          label: upperFirst(t('Support:Common.resolutionStatus.closed')),
          color: colors.blackroomGray[7],
          value: QuestionResolutionStatus.Closed,
        },
      ],
      [t],
    );

  /* ******************************** CALLBACKS ******************************* */

  const renderQuestionResolutionStatusOption: SelectProps['renderOption'] =
    useCallback(
      ({ option }: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
        <Text
          style={{ color: (option as QuestionResolutionStatusOption).color }}
        >
          {option.label}
        </Text>
      ),
      [],
    );

  /* ******************************** RENDERING ******************************* */
  return (
    <Select
      defaultValue={selectedResolutionStatus}
      data={questionResolutionStatusOptions}
      renderOption={renderQuestionResolutionStatusOption}
      styles={{
        input: {
          color: questionResolutionStatusOptions.find(
            (option) => option.value === selectedResolutionStatus,
          )?.color,
        },
      }}
      {...props}
    />
  );
}

export default QuestionResolutionStatusSelect;
